import {useEffect, useMemo, useState} from "react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faBinoculars, faCalendarDays, faClock} from "@fortawesome/free-solid-svg-icons";
import {useNavigate} from "react-router-dom";
import {ExplorerHero, ExplorerHeroStat, ExplorerHeroStats} from "../../../components/explorer-hero";
import CalculationLoading from "../../../components/calculation-loading";
import UpcomingTargetList from "../../../components/upcoming-target-list";
import {getRandomDashboardHeroImage} from "../../../helpers/dashboardHeroImages";
import {buildUpcomingTargets} from "../../../helpers/upcoming-targets/helpers";
import {UPCOMING_TARGET_HOME_RADIUS_MILES} from "../../../helpers/upcoming-targets/constants";
import type {UpcomingTargetsSummary} from "../../../helpers/upcoming-targets/types";
import type {UpcomingTargetsViewProps} from "./types";
import './upcoming-targets.scss';

export default function UpcomingTargetsView({rawData, calibrationResult, checklist, speciesImages, homeLocation}: UpcomingTargetsViewProps) {
    const navigate = useNavigate();
    const heroImage = useMemo(() => getRandomDashboardHeroImage(), []);
    const [targets, setTargets] = useState<UpcomingTargetsSummary>();

    useEffect(() => {
        let isActive = true;
        setTargets(undefined);
        const timeoutId = window.setTimeout(() => {
            const nextTargets = buildUpcomingTargets(rawData, checklist, calibrationResult, undefined, homeLocation);
            if (isActive) setTargets(nextTargets);
        }, 0);

        return () => {
            isActive = false;
            window.clearTimeout(timeoutId);
        };
    }, [rawData, calibrationResult, checklist, homeLocation]);

    if (!targets) {
        return <div className="discover upcoming-targets"><CalculationLoading title="Finding upcoming targets" message="Reviewing your past first sightings and seasonal patterns." /></div>;
    }

    const targetCount = targets.dueNow.length + targets.comingSoon.length;
    return (
        <div className="discover upcoming-targets">
            <ExplorerHero backgroundImage={heroImage} eyebrow="Discover" title="Upcoming Targets" intro="Birds your own history suggests may be worth looking for soon." backLabel="Discover" onBack={() => navigate('/discover')}>
                <ExplorerHeroStats ariaLabel="Upcoming targets summary">
                    <ExplorerHeroStat icon={<FontAwesomeIcon icon={faBinoculars} />} label="Targets" value={targetCount.toLocaleString()} />
                    <ExplorerHeroStat icon={<FontAwesomeIcon icon={faClock} />} label="Due Now" value={targets.dueNow.length.toLocaleString()} />
                    <ExplorerHeroStat icon={<FontAwesomeIcon icon={faCalendarDays} />} label="Historical Years" value={targets.historicalYears.toLocaleString()} />
                </ExplorerHeroStats>
            </ExplorerHero>
            <p className="upcoming-targets__note">{targets.hasHomeLocation ? `Suggestions are based on prior first sightings within ${UPCOMING_TARGET_HOME_RADIUS_MILES.toLocaleString()} miles of your home location. Birds already recorded in ${targets.currentYear} are left out.` : 'Set a home location to see targets based on recurring nearby sightings.'}</p>
            <div className="upcoming-targets__layout">
                <UpcomingTargetList title="Due Now" intro="Your usual first-sighting date is within the next week." icon={faClock} targets={targets.dueNow} speciesImages={speciesImages} emptyMessage="No recurring first sightings are due in the next week." />
                <UpcomingTargetList title="Coming Soon" intro="These birds usually begin appearing in your records over the next few weeks." icon={faCalendarDays} targets={targets.comingSoon} speciesImages={speciesImages} emptyMessage="No additional recurring first sightings are coming up in this window." />
            </div>
        </div>
    );
}
